import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Section } from '~/eds/src/components/Section/Section';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';

import { ModelTitle } from '../../fields/shared/ModelTitle';
import { DEPRECATED_usePollModelProgress } from '../../hooks/DEPRECATED_usePollModelProgress';
import { Evaluate } from '../../ModelDetails/Evaluate';
import { ModelVersionInfo } from '../../ModelVersionInfo';
import { ModelVersion } from '../../types/model';
import { compareConfig } from '../../utils/config';
import { testIsNonOptimizedModel } from '../../utils/status';
import { PublishOptions } from './PublishOptions';

interface Props {
  modelVersion: ModelVersion;
}

export const ReviewPublishModelVersion = ({ modelVersion }: Props) => {
  const intl = useIntl();
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);
  const [enablePolling, setEnablePolling] = useState<boolean>(true);
  const dispatch = useDispatch();

  const editModelConfirmed = testIsNonOptimizedModel(modelVersion, [
    'suspended',
  ]);

  const modelId = modelVersion.modelId;

  const { data: model, isLoading: isLoadingModel } =
    api.endpoints.getPromptModel.useQuery({ modelId });

  const lastPublishVersion = Number(
    model?.currentPublished?.version_number ?? 0,
  );

  const {
    data: previousModelVersion = null,
    isLoading: isPreviousModelVersionLoading,
  } = api.endpoints.getPromptModelVersion.useQuery(
    {
      modelId,
      version: lastPublishVersion,
      highlightMethod,
    },
    {
      skip: lastPublishVersion <= 0 || !lastPublishVersion,
    },
  );

  const { progress, isLoading: isLoadingProgress } =
    DEPRECATED_usePollModelProgress({
      modelId: modelVersion.modelId,
      publishStatus: modelVersion.publishStatus,
      enablePolling,
    });

  const { publishStatus } = progress;
  const currentConfig = modelVersion.config;
  const previousConfig = previousModelVersion?.config;

  const hasOnlyFiltersChanged = useMemo(() => {
    if (previousConfig && currentConfig) {
      const { hasFiltersChanged, hasInstructionsChanged } = compareConfig(
        previousConfig,
        currentConfig,
      );

      return hasFiltersChanged && !hasInstructionsChanged;
    }
    return false;
  }, [previousConfig, currentConfig]);

  useEffect(() => {
    dispatch(
      slices.xRay.actions.setPublishMode(
        hasOnlyFiltersChanged ? 'new_docs_in_scope' : 'all_docs_in_scope',
      ),
    );
  }, [hasOnlyFiltersChanged]);

  const items = [
    {
      title: intl.formatMessage({
        id: 'ReviewPublish.generalInformation',
        defaultMessage: 'General Information',
        description: 'Title for general information section',
      }),
      content: (
        <ModelVersionInfo modelId={modelId} version={modelVersion.version} />
      ),
    },
    {
      title: intl.formatMessage({
        id: 'ReviewPublish.modelPerformance',
        defaultMessage: 'Model Performance',
        description: 'Title for model performance section',
      }),
      content: (
        <Evaluate
          readOnly
          mode="summary-only"
          modelId={modelId}
          testCases={modelVersion.testCases}
          version={modelVersion.version}
        />
      ),
    },
  ];

  return (
    <ContentContainer
      loadingContent={{
        isLoading:
          isPreviousModelVersionLoading || isLoadingProgress || isLoadingModel,
      }}
    >
      <>
        <ModelTitle
          modelVersion={modelVersion}
          modelName={model?.targetEntity?.name}
          publishStatus={publishStatus}
        />
        {editModelConfirmed && (
          <Section
            title={intl.formatMessage({
              id: 'ReviewPublish.reviewYourModel',
              defaultMessage: 'Review your model',
              description: 'Section title for reviewing the model',
            })}
          >
            <PublishOptions
              progress={progress}
              modelId={modelVersion.modelId}
              hasOnlyFiltersChanged={hasOnlyFiltersChanged}
              onStopPolling={setEnablePolling}
            />
          </Section>
        )}
        <Section
          title={intl.formatMessage({
            id: 'ReviewPublish.overviewOfYourModel',
            defaultMessage: 'Overview of your model',
            description: 'Section title for model overview',
          })}
        >
          <Accordion border="vertical" items={items} />
        </Section>
      </>
    </ContentContainer>
  );
};
